"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Check, Play, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const todoTasks = [{
    id: 1,
    title: "Audit OTA channels",
    tag: "Audit",
    tagColor: "bg-accent text-accent-foreground",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_avatar.png"
  }, {
    id: 2,
    title: "Tech stack review",
    tag: "Tech",
    tagColor: "bg-accent text-accent-foreground",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_avatar.png"
  }];
  const inProgressTasks = [{
    id: 3,
    title: "Revenue strategy",
    tag: "Strategy",
    tagColor: "bg-primary/20 text-primary",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_avatar.png"
  }, {
    id: 4,
    title: "Market analysis",
    tag: "Data",
    tagColor: "bg-primary/20 text-primary",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_avatar.png"
  }];
  const doneTasks = [{
    id: 5,
    title: "Booking growth",
    tag: "Result",
    tagColor: "bg-primary/20 text-primary",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_avatar.png"
  }];
  const trustItems = ["Data-driven insights", "OTA optimization", "Expert guidance"];
  return <section data-section-id="69" id="herorevenueexpertise" className="relative w-full overflow-hidden bg-background text-foreground min-h-[720px] flex items-center">
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "radial-gradient(hsl(222 47% 11%) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div aria-hidden="true" className="absolute -top-40 -right-40 w-[800px] h-[800px] rounded-lg pointer-events-none" style={{
      background: "radial-gradient(circle, hsla(43, 72%, 52%, 0.2) 0%, transparent 60%)",
      filter: "blur(60px)"
    }} />

      <div aria-hidden="true" className="absolute -bottom-40 -left-40 w-[700px] h-[700px] rounded-lg pointer-events-none" style={{
      background: "radial-gradient(circle, hsla(222, 47%, 25%, 0.2) 0%, transparent 60%)",
      filter: "blur(80px)"
    }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-12 lg:gap-20 items-center">
          <div ref={leftRef} className={`flex flex-col gap-8 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="outline" className="self-start rounded-lg border-border bg-muted/50 backdrop-blur-sm px-3 py-1.5 text-[13px] font-mono text-foreground/90">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-lg h-2 w-2 bg-primary" />
              </span>
              REVENUE EXPERT · Sunil Singh
            </Badge>

            <h1 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-[84px] font-semibold leading-[1.02] tracking-tight text-foreground">
              Maximize Your Hotel’s Revenue with{" "}
              <span className="bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, hsl(43 72% 52%) 0%, hsl(222 47% 25%) 100%)"
            }}>
                Data-Driven OTA Strategies.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-[520px] leading-relaxed">
              HotelRevBaBa provides expert revenue management consulting to help hotel owners and investors maximize profitability through smart tech and OTA optimization.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <Link to="/ContactPage">
                <Button size="lg" className="h-12 px-6 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-lg shadow-primary/20 w-full sm:w-auto">
                  Book a Consultation
                  <ArrowRight className="ml-1 size-4" />
                </Button>
              </Link>
              <Link to="/AboutSunilPage">
                <Button variant="ghost" size="lg" className="h-12 px-5 rounded-lg text-foreground hover:bg-muted hover:text-foreground font-medium text-base w-full sm:w-auto">
                  <span className="flex items-center justify-center size-7 rounded-lg bg-muted mr-1">
                    <Play className="size-3 fill-current" />
                  </span>
                  Meet Sunil Singh
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-2 font-mono text-[13px] text-muted-foreground">
              {trustItems.map((item, i) => <div data-index={i} key={item} className="flex items-center gap-1.5">
                  <Check className="size-3.5 text-primary" strokeWidth={2.5} />
                  <span>{item}</span>
                </div>)}
            </div>
          </div>

          <div ref={rightRef} className={`relative w-full transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative mx-auto w-full max-w-[580px] lg:rotate-[-2deg] origin-center" style={{
            filter: "drop-shadow(0 30px 60px hsla(0, 0%, 0%, 0.1))"
          }}>
              <Card className="bg-card border border-border rounded-xl overflow-hidden p-0 gap-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/50">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <span className="size-2.5 rounded-lg bg-destructive" />
                      <span className="size-2.5 rounded-lg bg-primary" />
                      <span className="size-2.5 rounded-lg bg-chart-2" />
                    </div>
                    <span className="ml-3 font-mono text-xs text-muted-foreground">
                      hotelrev / dashboard
                    </span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">K</span>
                </div>

                <CardContent className="p-4 grid grid-cols-3 gap-3">
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between px-1">
                      <span className="font-default text-xs font-semibold text-foreground uppercase tracking-wider">
                        Audit
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{todoTasks.length}</span>
                    </div>
                    {todoTasks.map((task, i) => <div data-index={i} key={task.id} className="bg-muted border border-border rounded-lg p-2.5 flex flex-col gap-2">
                        <p className="text-[11px] text-foreground leading-snug font-medium">
                          {task.title}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-lg font-mono ${task.tagColor}`}>
                            {task.tag}
                          </span>
                          <SafeHydrate><Avatar className="size-5 border border-background">
                            <AvatarImage src={task.avatar} alt="Sunil Singh" />
                            <AvatarFallback className="text-[8px]">SS</AvatarFallback>
                          </Avatar></SafeHydrate>
                        </div>
                      </div>)}
                  </div>

                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between px-1">
                      <span className="font-default text-xs font-semibold text-foreground uppercase tracking-wider">
                        Strategy
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{inProgressTasks.length}</span>
                    </div>
                    {inProgressTasks.map((task, i) => <div data-index={i} key={task.id} className="bg-muted border-l-2 border-l-primary border-t border-r border-b border-border rounded-lg p-2.5 flex flex-col gap-2">
                        <p className="text-[11px] text-foreground leading-snug font-medium">
                          {task.title}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-lg font-mono ${task.tagColor}`}>
                            {task.tag}
                          </span>
                          <SafeHydrate><Avatar className="size-5 border border-background">
                            <AvatarImage src={task.avatar} alt="Sunil Singh" />
                            <AvatarFallback className="text-[8px]">SS</AvatarFallback>
                          </Avatar></SafeHydrate>
                        </div>
                      </div>)}
                  </div>

                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between px-1">
                      <span className="font-default text-xs font-semibold text-foreground uppercase tracking-wider">
                        Growth
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{doneTasks.length}</span>
                    </div>
                    {doneTasks.map((task, i) => <div data-index={i} key={task.id} className="bg-muted/60 border border-border rounded-lg p-2.5 flex flex-col gap-2 opacity-80">
                        <p className="text-[11px] text-foreground leading-snug font-medium line-through decoration-muted-foreground/50">
                          {task.title}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-lg font-mono ${task.tagColor}`}>
                            <Check className="inline size-2 mr-0.5" strokeWidth={3} />
                            {task.tag}
                          </span>
                          <SafeHydrate><Avatar className="size-5 border border-background">
                            <AvatarImage src={task.avatar} alt="Sunil Singh" />
                            <AvatarFallback className="text-[8px]">SS</AvatarFallback>
                          </Avatar></SafeHydrate>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="hidden md:block absolute -top-6 -right-2 lg:-right-8 w-[240px] lg:rotate-[4deg] origin-center z-20" style={{
            filter: "drop-shadow(0 20px 40px hsla(0, 0%, 0%, 0.05))"
          }}>
              <Card className="bg-card border border-primary/30 rounded-xl p-0 gap-0">
                <CardContent className="p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center justify-center size-6 rounded-lg bg-primary/20">
                      <Sparkles className="size-3 text-primary" />
                    </div>
                    <span className="font-default text-xs font-semibold text-foreground">
                      Revenue Insight
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
                    Optimized OTA listings increased <span className="text-primary font-medium">direct bookings</span> by 24% this quarter.
                  </p>
                  <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                    <span className="size-1.5 rounded-lg bg-primary animate-pulse" />
                    Updated today
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="hidden md:block absolute -bottom-6 -left-2 lg:-left-10 w-[220px] lg:rotate-[-3deg] origin-center z-20" style={{
            filter: "drop-shadow(0 20px 40px hsla(0, 0%, 0%, 0.05))"
          }}>
              <Card className="bg-card border border-border rounded-xl p-0 gap-0">
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="font-default text-xs font-semibold text-foreground">
                      RevPAR Growth
                    </span>
                    <span className="font-mono text-[10px] text-primary">+18%</span>
                  </div>
                  <div className="flex items-end gap-1 h-12">
                    {[40, 55, 35, 65, 50, 75, 90].map((h, i) => <div data-index={i} key={i} className="flex-1 rounded-lg" style={{
                    height: `${h}%`,
                    background: i === 6 ? "linear-gradient(180deg, hsl(43 72% 52%) 0%, hsl(222 47% 25%) 100%)" : "hsl(214 18% 92%)"
                  }} />)}
                  </div>
                  <div className="flex justify-between mt-1.5 font-mono text-[9px] text-muted-foreground">
                    <span>Jan</span>
                    <span>Jun</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}